import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const HERO_URL =
  "https://images.unsplash.com/photo-1534359265607-b9cdb5e0a81e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHx1ayUyMExvbmRvbiUyMGNpdHklMjB0cmF2ZWwlMjBidXMlMjB0cmFpbiUyMG1vZGVybiUyMHRyYW5zcG9ydHxlbnwwfHx8fDE3ODk4NDg2NTl8MA&ixlib=rb-4.1.0&q=85";

const ROLE_OPTIONS = [
  { value: "travel_trainer", label: "Staff / Trainer" },
  { value: "admin_manager", label: "Co-ordinator" },
  { value: "super_admin", label: "Manager" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [selectedRole, setSelectedRole] = useState("travel_trainer");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password, selectedRole);
      toast.success("Signed in successfully");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="flex items-center justify-center px-4 sm:px-8 py-10">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <img src="/deluxe-logo.png" alt="Deluxe Healthcare Services" className="h-14 w-auto" data-testid="login-logo" />
            <p className="mt-2 text-sm text-slate-600">Independent Travel Training for SEND</p>
          </div>

          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 mb-2">
            Staff sign in
          </h2>
          <p className="text-base text-slate-600 mb-6">
            Secure access for authorised staff only. All sign-ins are recorded.
          </p>

          {error && (
            <div
              data-testid="login-error-alert"
              role="alert"
              className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            >
              {error}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label id="login-role-label">Sign in as</Label>
              <div
                role="radiogroup"
                aria-labelledby="login-role-label"
                data-testid="login-role-selector"
                className="grid grid-cols-3 gap-1 rounded-md border border-slate-200 bg-slate-50 p-1"
              >
                {ROLE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={selectedRole === opt.value}
                    data-testid={`login-role-${opt.value}`}
                    onClick={() => setSelectedRole(opt.value)}
                    className={`rounded px-2 py-2 text-xs sm:text-sm font-medium transition-colors duration-150 min-h-[44px] ${
                      selectedRole === opt.value
                        ? "bg-primary text-white shadow-sm"
                        : "text-slate-600 hover:bg-white"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 focus:ring-2 focus:ring-cyan-700"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 pr-11 focus:ring-2 focus:ring-cyan-700"
                />
                <button
                  type="button"
                  data-testid="login-password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-700 rounded"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-primary hover:bg-brand-hover text-white font-semibold"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <div className="mt-4 text-sm">
            <Link
              to="/forgot-password"
              data-testid="forgot-password-link"
              className="font-medium text-primary hover:text-brand-hover underline-offset-4 hover:underline"
            >
              Forgotten your password?
            </Link>
          </div>

          <p className="mt-4 text-center text-xs text-slate-500" data-testid="login-support-contact">
            Need help signing in? Contact <a href="mailto:info@deluxehealthcare.co.uk" className="font-medium text-primary hover:underline">info@deluxehealthcare.co.uk</a>
          </p>

          <div className="mt-8 flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
            <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
            <p>
              This system contains confidential information about children. Unauthorised access is prohibited and
              all activity is monitored and recorded.
            </p>
          </div>
        </div>
      </div>

      <div className="relative hidden lg:block">
        <img src={HERO_URL} alt="Passengers travelling safely on a UK bus" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-cyan-950/90 via-slate-900/60 to-slate-900/30" />
        <div className="relative z-10 flex h-full flex-col justify-end p-12">
          <blockquote className="max-w-lg">
            <p className="font-heading text-3xl font-bold text-white leading-snug">
              Building independence, one journey at a time.
            </p>
            <p className="mt-4 text-base text-slate-200">
              Working with UK local authorities to teach children and young people with SEND to travel safely and
              independently between home and school.
            </p>
          </blockquote>
        </div>
      </div>
    </div>
  );
}
